import ExternalLinks from './ExternalLinks';
import Pill from './Pill';
import { useNavigate } from 'react-router-dom';
import BlurHashAvatar from './BlurHashAvatar';

interface PractionerProps {
  provider: {
    id: string;
    provider_name: string;
    provider_description: string;
    provider_logo?: string;
    provider_instagram?: string;
    providers_tiktok?: string;
    provider_link?: string;
    provider_country?: string;
    provider_region?: string;
    provider_postcode?: string;
    approved?: boolean;
    owner_id?: string;
    background_image?: string;
    provider_logo_blur_hash?: string;
  };
  hoverable?: boolean;
  pills?: Array<{
    text: string;
    colorClass: string;
    size?: 'small' | 'medium' | 'large';}>;
  clickable?: boolean;
}

const PractitionerCard: React.FC<PractionerProps> = ({ provider, hoverable = true, pills, clickable = true }) => {
  const navigate = useNavigate();

  const truncateDescription = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
  };

  return (
    <div 
      onClick={() => clickable ? navigate(`/practitioners/view?id=${provider.id}`) : null}
      className={`
        rounded-lg border border-muted-grey p-6 flex gap-4 max-w-2xl transition-shadow
        ${hoverable 
          ? "bg-muted-lightGrey hover:bg-white transition-background duration-300 hover:shadow-[10px_10px_0_-2px_theme(colors.muted.grey)]" 
          : "bg-white shadow-[10px_10px_0_-2px_theme(colors.muted.grey)]"
        }
      `}
    >
      <div className="flex-shrink-0 flex flex-col justify-between">
        <div className="w-16 h-16 overflow-hidden rounded-lg">
          <BlurHashAvatar
            src={provider.provider_logo}
            blurHash={provider.provider_logo_blur_hash}
            alt={`${provider.provider_name} logo`}
            roundedClassName="rounded-full"
            borderClassName="border-[5px] border-muted-sage"
            className="object-cover object-contain greyscale-[70%]"
          />
        </div>
        <ExternalLinks
          instagram={provider.provider_instagram}
          tiktok={provider.providers_tiktok}
          website={provider.provider_link}
        />
      </div>

      <div className="flex-1 flex flex-col">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-1">{provider.provider_name}</h3>
          <p className="text-gray-600 mb-4">
            {provider.provider_description && truncateDescription(provider.provider_description, 140)}
          </p>
        </div>

        <div className="mt-auto">
          <div className="flex items-center justify-end mb-2">
            <div className="text-gray-600 hover:text-gray-900">
              {provider.provider_country && provider.provider_region && (
                <span>
                  {provider.provider_country}, {provider.provider_region}
                </span>
              )}
            </div>
          </div>
          
          {pills && pills.length > 0 && (
            <div className="flex gap-2 flex-wrap justify-end">
              {pills.map((pill, index) => (
                <Pill
                  key={`${pill.text}-${index}`}
                  text={pill.text}
                  colorClass={pill.colorClass}
                  size={pill.size}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PractitionerCard;